'use client';

import { QueryKeys, SocialLoginUrls } from '@/enums';
import { openNotification } from '@/helpers/mantine-notification-helper';
import { toAddToCartPayloads, toStandaloneAddToCartPayloads } from '@/helpers/guest-cart-helper';
import { IAddToCartItem, IGetCartProduct } from '@/interfaces/cart';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { signOut, useSession } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import { useEffect, useState, useMemo } from 'react';
import { REDIRECT_BY_RTK_ERROR_STRING, SESSION_STALE_TIME } from '@/constants';
import { ICustomAuthHookModel, INextAuthSessionModel, IUseSessionModel } from '@/interfaces/authentication';
import { getSessionFromNextServer } from '@/services/authentication.service';
import { clearAllCookies } from '@/utils/helper';
import { addToCart } from '@/services/cart.service';

export async function fetchSession(queryParamString?: string): Promise<INextAuthSessionModel | null> {
  const session = (await getSessionFromNextServer(queryParamString)).data;
  if (Object.keys(session).length) {
    return session;
  }
  return null;
}
// const { status } = useSession();
export function useReactQuerySession({
  required = false,
  redirectTo = '/?error=' + REDIRECT_BY_RTK_ERROR_STRING,
  queryConfig = { queryKey: [QueryKeys.session] },
}: IUseSessionModel = {}): ICustomAuthHookModel {
  const queryClient = useQueryClient();
  const [loading, isLoading] = useState(false);
  const router = useRouter();
  const { data: session, status } = useSession();
  // Safely stabilize the queryKey to prevent infinite loops from literal arrays
  const keyHash = useMemo(() => {
    try {
      return JSON.stringify(queryConfig?.queryKey);
    } catch {
      return 'react-query-session-fallback-query-key';
    }
  }, [queryConfig?.queryKey]);

  const queryKey = useMemo(() => queryConfig?.queryKey || [QueryKeys.session], [keyHash]);

  useEffect(() => {
    if (status !== 'loading') {
      const currentData = queryClient.getQueryData(queryKey);
      // Only update if the session object reference is actually different
      if (currentData !== session) {
        queryClient.setQueryData(queryKey, session);
      }
    }
  }, [session, status, queryClient, queryKey]);

  const query = useQuery<INextAuthSessionModel | null>({
    staleTime: SESSION_STALE_TIME,
    refetchIntervalInBackground: true,
    ...queryConfig,
    queryKey,
    queryFn: () => {
      return queryClient.getQueryData<INextAuthSessionModel | null>(queryKey) ?? (session as INextAuthSessionModel) ?? null;
    },
    enabled: status !== 'loading',
  });

  // Handle onSettled logic
  useEffect(() => {
    if (query.isSuccess && !query.data && required) {
      router.push(redirectTo);
    }
  }, [query.isSuccess, query.data, required, router, redirectTo]);

  useEffect(() => {
    const isSocialLogin = SocialLoginUrls.GoogleLogin.includes(globalThis.history.state.__PRIVATE_NEXTJS_INTERNALS_TREE[1].children[0]);

    if (query.data?.error === 'RefreshAccessTokenError') {
      signOut({ callbackUrl: '/', redirect: false }).then(() => {
        clearAllCookies();
        globalThis.location.href = '/';
      });
    } else if (query.data?.accessToken) {
      let stringifiedCartItems = localStorage.getItem('cart');

      if (stringifiedCartItems) {
        try {
          let tempCartItems = JSON.parse(stringifiedCartItems) as IGetCartProduct;

          if (tempCartItems.Items) {
            let cartItems: IGetCartProduct = tempCartItems;

            const syncCartItem = (payload: IAddToCartItem) => {
              isLoading(true);

              addToCart(payload, query.data)
                .then((x) => {
                  console.log(x, 'done');
                  queryClient.invalidateQueries({ queryKey: [QueryKeys.cartItems, query.data?.accessToken || 'anonymous'] });
                  queryClient.invalidateQueries({ queryKey: [QueryKeys.orderSummary] });
                })
                .catch((data: AxiosError) => {
                  console.log(data, 'error');
                  if (payload.addtoCartBundleInfo) {
                    toStandaloneAddToCartPayloads(payload).forEach(syncCartItem);
                    return;
                  }
                  openNotification(
                    'danger',
                    'Add to cart failed.',
                    (data.response?.data as { Messages: string[] })?.Messages[0] ||
                      'Cart item sync encountered an error, please try adding the item again.',
                  );
                })
                .finally(() => {
                  localStorage.removeItem('cart');

                  isLoading(false);
                });
            };

            toAddToCartPayloads(cartItems).forEach(syncCartItem);
            localStorage.removeItem('cart');
          } else {
            localStorage.removeItem('cart');
          }
        } catch (e) {
          isLoading(false);
          localStorage.removeItem('cart');
          console.error('Checkout error:', e);
        }
      }
      else if (isSocialLogin) {
        // wait 2 seconds to close so the network call initiates
        setTimeout(() => window.close(), 2000);
      }
    }
  }, [query.data]);

  let authObject = {
    // isAuthenticated: query.data ? (query.data?.error ? false : true) : false,
    isAuthenticated: query.data ? true : false,
    session: query.data,
    loading: query.status === 'pending',
    addToCartLoading: loading,
  };

  return authObject;
}
